/**
 * Category colour — every colour that names a CATEGORY on screen (a calendar event's visit type / appointment profile,
 * its small location glyph, a chart series) comes from the muted Okabe-Ito ramp, `--cat-<hue>-<step>` (Antonin,
 * 13 Oct: "make sure the charts, the calendar events, their small icons etc use the palette defined as okabe ito
 * muted").
 *
 * Profiles keep the colour the clinic picked (a saturated Flutter colour, stored as it is: nothing is rewritten on
 * the server). At display, the picked colour is read for its HUE only and drawn in the nearest muted hue, so a clinic
 * that chose "orange" still sees orange, in the system's tone. A grey or unreadable colour has no hue: the caller's
 * fallback is used. Status never takes these colours (rule: colour is for category, status is a shape + its own hue).
 *
 *   100 tint (backgrounds) · 200 hover tint · 500 edge, bar, swatch · 600 icon · 800 text on the tint
 */
export type CategoryHue = 'orange' | 'jade' | 'purple' | 'vermilion' | 'sky' | 'yellow' | 'blue';
export type CategoryStep = 100 | 200 | 400 | 500 | 600 | 800;

/** The order series take hues in, for charts and anything indexed (blue last, as in the practitioner app). */
export const CATEGORY_ORDER: readonly CategoryHue[] = ['orange', 'jade', 'purple', 'vermilion', 'sky', 'yellow', 'blue'];

/**
 * The hex each hue's step-500 token resolves to (the swatch). The settings pickers SAVE this value (the backend stores
 * a colour string, not a token name) and `categoryHue` recognises it exactly, so a saved swatch always re-reads as the
 * same hue and shows as selected. Keep in step with `--cat-<hue>-500` in `styles/showcase-bridge.css`.
 */
export const CATEGORY_500_HEX: Readonly<Record<CategoryHue, string>> = {
  orange: '#BD975F',
  jade: '#5B947C',
  purple: '#AF8199',
  vermilion: '#B57454',
  sky: '#74A2BE',
  yellow: '#C3BE75',
  blue: '#507B9E',
};

const HUE_LABEL: Readonly<Record<CategoryHue, string>> = {
  orange: 'Orange', jade: 'Jade', purple: 'Purple', vermilion: 'Vermilion', sky: 'Sky', yellow: 'Yellow', blue: 'Blue',
};

function parseRgb(color: string): [number, number, number] | null {
  const c = color.trim().replace(/^0x/i, ''); // Flutter `0xAARRGGBB`
  let m = /^#?([0-9a-f]{3})$/i.exec(c);
  if (m) return [0, 1, 2].map((i) => parseInt(m![1][i] + m![1][i], 16)) as [number, number, number];
  m = /^#?(?:[0-9a-f]{2})?([0-9a-f]{6})$/i.exec(c); // #rrggbb, or #aarrggbb (Flutter ARGB)
  if (m) return [0, 2, 4].map((i) => parseInt(m![1].slice(i, i + 2), 16)) as [number, number, number];
  m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(c);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
}

/** The muted hue nearest to a picked colour's hue; null for greys and anything unreadable. */
export function categoryHue(color?: string | null): CategoryHue | null {
  if (!color) return null;
  const rgb = parseRgb(color);
  if (!rgb) return null;
  // A saved swatch is read back as its own hue (blue's 500 sits right on the blue/sky boundary).
  const hex = '#' + rgb.map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
  const exact = CATEGORY_ORDER.find((h) => CATEGORY_500_HEX[h] === hex);
  if (exact) return exact;
  const [r, g, b] = rgb.map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (s < 0.15 || d < 0.08) return null; // a grey: no category
  let h = 0;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  if (h < 15 || h >= 345) return 'vermilion';
  if (h < 32) return 'vermilion';
  if (h < 50) return 'orange';
  if (h < 75) return 'yellow';
  if (h < 185) return 'jade'; // teal reads as green-blue: jade
  if (h < 212) return 'sky';
  if (h < 262) return 'blue';
  return 'purple';
}

/** `var(--cat-<hue>-<step>)` for a picked colour, else the fallback (a neutral token by default). */
export function categoryColor(color: string | null | undefined, step: CategoryStep, fallback = 'var(--color-grey-500)'): string {
  const hue = categoryHue(color);
  return hue ? `var(--cat-${hue}-${step})` : fallback;
}

/** The nth series colour (charts). */
export function seriesColor(index: number, step: CategoryStep = 500): string {
  return `var(--cat-${CATEGORY_ORDER[index % CATEGORY_ORDER.length]}-${step})`;
}

/**
 * The profile colours the backend has always been sent (Flutter `Color(int)` ARGB, the captured cURL palette). The
 * Flutter apps draw exactly these, so the pickers keep WRITING them; only the display is muted. Each is the value a
 * swatch saves when that swatch is the hue this colour displays as (`categoryHue`).
 */
export const FLUTTER_PROFILE_COLORS = {
  softBlue: '0xFFA7CAFF',
  lime: '0xFFB6E300',
  green: '0xFF65D24A',
  orange: '0xFFFF9B2B',
  salmon: '0xFFEE6464',
  yellow: '0xFFFCC001',
} as const;

/** A new profile's colour, as before the restyle (the first Flutter preset). */
export const DEFAULT_PROFILE_COLOR: string = FLUTTER_PROFILE_COLORS.softBlue;

/** Swatch → the Flutter preset that displays as it. Sky and purple have none: no stored preset reads as them. */
const FLUTTER_WIRE_BY_HUE: Readonly<Partial<Record<CategoryHue, string>>> = {
  blue: FLUTTER_PROFILE_COLORS.softBlue,
  yellow: FLUTTER_PROFILE_COLORS.lime,
  jade: FLUTTER_PROFILE_COLORS.green,
  vermilion: FLUTTER_PROFILE_COLORS.salmon,
  orange: FLUTTER_PROFILE_COLORS.yellow,
};

/** What a settings colour picker offers: one swatch per muted hue, in series order. Drawn in the step-500 token. */
export const CATEGORY_SWATCHES: ReadonlyArray<{ hue: CategoryHue; label: string; css: string; wire: string }> =
  CATEGORY_ORDER.map((hue) => ({
    hue,
    label: HUE_LABEL[hue],
    css: `var(--cat-${hue}-500)`,
    wire: categoryWireColor(hue),
  }));

/**
 * The value a picker stores for a hue: Flutter `0xAARRGGBB` (the wire format the Flutter apps parse), opaque. Where a
 * Flutter preset displays as this hue, that preset is stored, so the backend keeps receiving the values it always has.
 */
export function categoryWireColor(hue: CategoryHue): string {
  return FLUTTER_WIRE_BY_HUE[hue] ?? '0xFF' + CATEGORY_500_HEX[hue].slice(1);
}

/** The swatch a stored colour shows as selected: its nearest muted hue (null for a grey or unreadable value). */
export function selectedSwatchHue(stored: string | null | undefined): CategoryHue | null {
  return categoryHue(stored);
}
